/**
 * <rad-multibuy>: the "Choose your sizes" table (sections/rad-multibuy.liquid), 2026-10-15.
 *
 * - Grouped product: rows are server-rendered and already sorted; this only adds pagination and the basket post.
 * - Product kept per size (data-src): on first open of the <details>, reads page 1 then the rest in parallel, up to
 *   data-max-pages pages of
 *   sections/rad-multibuy-rows.liquid, keeps rows whose data-family equals this product's family, drops duplicates,
 *   sorts by height then width (numeric; a missing value sorts last) and marks the size on the page.
 * - "Add all to basket": every enabled row with a quantity above 0 goes to /cart/add.js as one items array, then the
 *   cart drawer opens. When Shopify refuses the batch, each item is retried alone so the visitor sees exactly which sizes
 *   could not be added and why. Enquiry-only and unavailable rows have no input, so they are never sent.
 * One live region (data-mb="status") carries every message. No layout shift: rows only appear inside the opened panel.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const DEFAULTS = {
  none: 'Enter a quantity for at least one size.',
  bad: 'Quantities must be whole numbers from 0 to 99.',
  adding: 'Adding to your basket.',
  added: 'Items added to your basket: %n.',
  partial: 'Items added: %n. Not added: %list.',
  failed: 'Nothing was added. %list.',
  network: 'Sorry, the basket could not be updated. Please try again or call us.',
  empty: 'No other sizes were found for this model.',
  load_error: 'Sizes could not be loaded. Please try again.',
  more: 'Show more sizes (%n more)',
  not_available: 'not available',
};
const fill = (s, n, list) => s.replace('%n', String(n ?? '')).replace('%list', list ?? '');

/** Numeric sort key: height, then width; missing values last. Exported for tests. */
export function compareRows(a, b) {
  const num = (v) => {
    const n = parseFloat(String(v ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : Infinity;
  };
  return num(a.h) - num(b.h) || num(a.w) - num(b.w) || 0;
}

/** Items to post: [{id, quantity}] from rows with an enabled input above 0. Exported for tests. */
export function collectItems(rows) {
  const items = [];
  let invalid = false;
  for (const r of rows) {
    if (r.disabled || r.qty === '' || r.qty == null) continue;
    const q = Number(r.qty);
    if (!Number.isInteger(q) || q < 0 || q > 99) { invalid = true; continue; }
    if (q > 0) items.push({ id: Number(r.id), quantity: q });
  }
  return { items, invalid };
}

class RadMultibuy extends HTMLElement {
  #abort = new AbortController();
  #loaded = false;
  #shown = 0;

  connectedCallback() {
    const signal = this.#abort.signal;
    this.pageSize = Number(this.dataset.pageSize) || 25;
    try {
      this.t = { ...DEFAULTS, ...JSON.parse(this.querySelector('[data-mb="i18n"]')?.textContent || '{}') };
    } catch {
      this.t = { ...DEFAULTS };
    }
    this.details = this.querySelector('details');
    this.tbody = this.querySelector('[data-mb="rows"]');
    this.status = this.querySelector('[data-mb="status"]');
    this.moreBtn = this.querySelector('[data-mb="more"]');
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.addAll(); }, { signal });
    this.moreBtn?.addEventListener('click', () => this.#reveal(this.pageSize), { signal });
    if (this.dataset.src) {
      const load = () => { if (this.details?.open && !this.#loaded) this.#load(); };
      this.details?.addEventListener('toggle', load, { signal });
      load();
    } else {
      this.#shown = this.#rows().filter((r) => !r.hidden).length;
      this.#updateMore();
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #rows() {
    return [...(this.tbody?.querySelectorAll('tr[data-variant-id]') ?? [])];
  }

  #say(text, isError = false) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.classList.toggle('rad-mb__status--error', isError);
  }

  async #load() {
    this.#loaded = true;
    const loading = this.querySelector('[data-mb="loading"]');
    const family = this.dataset.family || '';
    const maxPages = Math.min(Number(this.dataset.maxPages) || 7, 8);
    const seen = new Set();
    const found = [];
    const read = async (page) => {
      const url = new URL(this.dataset.src, window.location.origin);
      url.searchParams.set('page', String(page));
      const res = await fetch(url, { signal: this.#abort.signal, headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    };
    const keep = (doc) => {
      for (const tr of doc.querySelectorAll('tr[data-variant-id]')) {
        if (tr.dataset.family !== family || seen.has(tr.dataset.variantId)) continue;
        seen.add(tr.dataset.variantId);
        found.push(tr);
      }
    };
    try {
      const first = await read(1);
      keep(first);
      const pages = Math.min(Number(first.querySelector('tbody[data-pages]')?.dataset.pages) || 1, maxPages);
      const rest = await Promise.all(Array.from({ length: Math.max(pages - 1, 0) }, (_, i) => read(i + 2)));
      rest.forEach(keep);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#loaded = false;
      if (loading) loading.hidden = true;
      this.#say(this.t.load_error, true);
      return;
    }
    found.sort((a, b) => compareRows(a.dataset, b.dataset));
    const pid = this.dataset.productId;
    const frag = document.createDocumentFragment();
    found.forEach((tr, i) => {
      const row = document.importNode(tr, true);
      if (row.dataset.productId === pid) {
        row.classList.add('rad-mb__row--current');
        row.setAttribute('aria-current', 'true');
        row.querySelector('.rad-mb__this')?.removeAttribute('hidden');
      }
      row.hidden = i >= this.pageSize;
      frag.append(row);
    });
    this.tbody.replaceChildren(frag);
    if (loading) loading.hidden = true;
    this.#shown = Math.min(found.length, this.pageSize);
    this.#updateMore();
    if (found.length <= 1) this.#say(this.t.empty);
  }

  #reveal(n) {
    const hidden = this.#rows().filter((r) => r.hidden);
    hidden.slice(0, n).forEach((r) => { r.hidden = false; });
    const first = hidden[0]?.querySelector('input.rad-mb__qty, th');
    this.#shown += Math.min(n, hidden.length);
    this.#updateMore();
    if (first instanceof HTMLElement) first.focus({ preventScroll: false });
  }

  #updateMore() {
    if (!this.moreBtn) return;
    const left = this.#rows().filter((r) => r.hidden).length;
    this.moreBtn.hidden = left === 0;
    this.moreBtn.textContent = fill(this.t.more, left);
  }

  #rowData() {
    return this.#rows().map((tr) => {
      const input = tr.querySelector('input.rad-mb__qty');
      return { tr, id: tr.dataset.variantId, size: tr.dataset.size, disabled: !input, qty: input?.value ?? '' };
    });
  }

  async addAll() {
    const rows = this.#rowData();
    rows.forEach((r) => r.tr.classList.remove('rad-mb__row--error'));
    const { items, invalid } = collectItems(rows);
    if (invalid) return this.#say(this.t.bad, true);
    if (!items.length) return this.#say(this.t.none, true);
    const addBtn = this.querySelector('[data-mb="add"]');
    if (addBtn) addBtn.disabled = true;
    this.#say(this.t.adding);
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));
    const failures = [];
    let added = 0;
    let lastSections;
    try {
      const before = await this.#cart();
      const res = await this.#post(items, sections);
      if (res.ok) {
        added = items.reduce((s, i) => s + i.quantity, 0);
        lastSections = res.body.sections;
      } else {
        // never re-send an item the refused batch already added: compare line quantities with the snapshot
        const after = await this.#cart();
        const qty = (c, id) => (c?.items ?? []).filter((l) => l.variant_id === id).reduce((s, l) => s + l.quantity, 0);
        for (const item of items) {   // tell the visitor exactly which size failed and why
          if (before && after && qty(after, item.id) - qty(before, item.id) >= item.quantity) { added += item.quantity; continue; }
          const one = await this.#post([item], sections);
          if (one.ok) { added += item.quantity; lastSections = one.body.sections; continue; }
          const row = rows.find((r) => Number(r.id) === item.id);
          row?.tr.classList.add('rad-mb__row--error');
          failures.push(`${row?.size || item.id}: ${one.body.description || one.body.message || this.t.not_available}`);
        }
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
      if (addBtn) addBtn.disabled = false;
      return this.#say(this.t.network, true);
    }
    const cart = await this.#cart();
    deferred.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
      detail: {
        items: cart?.items ?? [],
        source: 'rad-multibuy',
        sourceId: this.dataset.productId,
        itemCount: added,
        productId: this.dataset.productId,
        sections: lastSections,
        didError: added === 0,
      },
    });
    if (failures.length) {
      this.dispatchEvent(new CartErrorEvent({ error: failures.join('; '), code: 'INVALID' }));
    }
    if (addBtn) addBtn.disabled = false;
    if (added && !failures.length) this.#say(fill(this.t.added, added));
    else if (added) this.#say(fill(this.t.partial, added, failures.join('; ')), true);
    else this.#say(fill(this.t.failed, 0, failures.join('; ')), true);
    if (added) {
      rows.forEach((r) => { const i = r.tr.querySelector('input.rad-mb__qty'); if (i && !r.tr.classList.contains('rad-mb__row--error')) i.value = '0'; });
      this.#openDrawer();
    }
  }

  async #cart() {
    const root = window.Shopify?.routes?.root || '/';
    return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
  }

  async #post(items, sections) {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections }),
    });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok && !body.status, body };
  }

  #openDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      if (!drawer.isOpen) drawer.open();
    } else {
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    }
  }
}

if (!customElements.get('rad-multibuy')) customElements.define('rad-multibuy', RadMultibuy);
